import React, {useCallback, useMemo, useState} from 'react';

import {List} from 'immutable';
import uniq from 'lodash/uniq';

import MultiSelect, {useOnSearch} from '@components/multi-select';

const cannedSkills = [
  'Lead In',
  'Establish Direction',
  'Engage Prospect',
  'Clarify Outcome',
  'Next Steps',
  'Filler Words',
  'Active Listening',
  'Discovery',
  'Challenges',
  'Value Prop',
  'Solution Fit',
];

const toOption = value => ({
  title: value,
  value,
});

const valueToOption = value => ({
  title: `Add "${value}"`,
  value,
});

const getValue = i => i.value;

interface Props {
  onChange: (newValue: List<string>) => void;
  searchSkills: (searchTerm: string) => Promise<string[]>;
  value: List<string>;
}

export const SkillsField: React.FC<Props> = ({onChange, searchSkills, value, ...props}) => {
  const [foundSkills, setFoundSkills] = useState<string[]>([]);
  const [createdSkills, setCreatedSkills] = useState<string[]>([]);

  const searchFail = useCallback(
    error => {
      console.error(error);
      setFoundSkills([]);
    },
    [setFoundSkills]
  );

  const {loading, onLoad} = useOnSearch(searchSkills, setFoundSkills, searchFail);

  const jsValue = useMemo(() => (value?.toJS ? value.toJS() : []), [value]);
  const skills = useMemo(() => {
    return uniq([...cannedSkills, ...foundSkills, ...jsValue, ...createdSkills]);
  }, [createdSkills, foundSkills, jsValue]);

  const handleChange = useCallback(
    values => {
      onChange(List(values));
    },
    [onChange]
  );

  const handleCreate = useCallback(
    option => {
      setCreatedSkills(s => uniq([...s, option.value]));
      onChange((value || List([])).push(option.value));
    },
    [onChange, value]
  );

  const options = useMemo(() => skills.map(toOption), [skills]);

  return (
    <MultiSelect
      {...props}
      placeholder="Create or search existing skills..."
      onLoad={onLoad}
      loading={loading}
      fullWidth
      getValue={getValue}
      onCreate={handleCreate}
      valueToOption={valueToOption}
      options={options}
      onChange={handleChange}
      value={jsValue}
    />
  );
};
